import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { platformAPI } from '../../../services/platformAPI';
import tenderAPI from '../../../services/tenderAPI';
import EvidencePicker, { EvidenceLinks } from './EvidencePicker';
import { useWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import { currencyChoices, loadOrganisationSettings } from '../../../utils/organisationSettings';
import { AdminPickCell, AdminPlainBar, AdminRowDelete, useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';

const showDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const showAmount = (currency, amount) => {
  const number = Number(amount);
  const formatted = Number.isFinite(number) ? number.toLocaleString('en-IN') : '—';
  return `${currency || ''} ${formatted}`.trim();
};

const errText = (error, fallback) => error.response?.data?.message || fallback;

export function SecuritiesPanel({ tenderId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [issuerQuery, setIssuerQuery] = useState('');
  const [tenderQuery, setTenderQuery] = useState('');
  const [tenders, setTenders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [currencyList, setCurrencyList] = useState(['INR', 'USD', 'EUR', 'GBP', 'CAD', 'AUD']);
  const [form, setForm] = useState({
    tenderId: tenderId || '', typeCode: 'emd', amount: '', currency: 'INR', issuerCode: '', issuerName: '',
    instrumentReference: '', beneficiary: '', issueDate: '', expiryDate: '', claimExpiryDate: '', remarks: '',
    settlement: 'cash', physicalDueOn: '', physicalAddress: '', trackingReference: ''
  });
  const [refund, setRefund] = useState({ instrumentId: '', kind: 'received', amount: '', reason: '', reference: '', destinationInstrumentId: '' });
  const [refundEvidence, setRefundEvidence] = useState([]);
  const load = () => platformAPI.listSecurities(tenderId ? { tenderId } : {})
    .then((response) => setData(response.data?.data))
    .catch((error) => setError(errText(error, 'Unable to load securities')));
  useEffect(() => { load(); }, [tenderId]);
  useEffect(() => {
    if (tenderId) return undefined;
    tenderAPI.getTenders({ limit: 100 })
      .then((res) => setTenders(res?.data?.tenders || res?.tenders || []))
      .catch(() => setTenders([]));
    return undefined;
  }, [tenderId]);
  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const settings = data?.settings;
      if (settings?.tenderCurrencies?.length) setCurrencyList(settings.tenderCurrencies);
      if (settings?.currency) {
        setForm((current) => (current.currency === 'INR' ? { ...current, currency: settings.currency } : current));
      }
    });
  }, []);
  const save = async (event) => {
    event.preventDefault();
    try {
      await platformAPI.saveSecurity({
        ...form,
        tenderId: form.tenderId || tenderId,
        documents: documents.filter((item) => item.documentId)
      });
      setError('');
      setDocuments([]);
      await load();
    } catch (error) {
      setError(errText(error, 'Unable to save the instrument'));
    }
  };
  const act = async (id, kind) => {
    const note = window.prompt(kind === 'extend' ? 'New expiry date (YYYY-MM-DD)' : kind === 'release' ? 'Release amount' : 'Confirm') ;
    if (!note) return;
    try {
      if (kind === 'extend') await platformAPI.extendSecurity(id, { newExpiry: note });
      if (kind === 'release') await platformAPI.releaseSecurity(id, { amount: note });
      if (kind === 'expired' || kind === 'invoked') await platformAPI.markSecurity(id, { status: kind });
      await load();
    } catch (error) {
      setError(errText(error, 'Unable to update the instrument'));
    }
  };
  const issuers = useMemo(() => (data?.issuers || []).filter((row) => row.status === 'active' && (!issuerQuery || row.label.toLowerCase().includes(issuerQuery.toLowerCase()))), [data, issuerQuery]);
  const tenderChoices = useMemo(() => {
    const query = tenderQuery.trim().toLowerCase();
    return tenders.filter((row) => !query || `${row.reference || ''} ${row.title || ''}`.toLowerCase().includes(query));
  }, [tenders, tenderQuery]);
  const currencies = currencyChoices(currencyList, form.currency);
  const admin = useAdminRecordDelete({
    entity: 'security',
    noun: 'instrument',
    plural: 'instruments',
    reload: load,
    label: (row) => row.instrumentReference || row.typeLabel || 'Instrument'
  });
  if (!data) return <Spinner animation="border" size="sm" />;
  const instruments = data.instruments || [];
  return (
    <div className="bg-register">
      <p className="bg-register__note">Tracking only. This does not issue a bank instrument, take a payment, or post an accounting entry. Amounts in different currencies stay separate.</p>
      {error ? <Alert variant="warning">{error}</Alert> : null}
      <div className="bg-register__stats">
        <article className="bg-register__stat">
          <span>Cash outstanding</span>
          <strong>{data.cashOutstanding?.length ? data.cashOutstanding.map((row) => showAmount(row.currency, row.amount)).join(' · ') : 'None'}</strong>
        </article>
        <article className="bg-register__stat">
          <span>Cash held</span>
          <strong>{data.cashHeld?.length ? data.cashHeld.map((row) => showAmount(row.currency, row.amount)).join(' · ') : 'None'}</strong>
        </article>
        <article className="bg-register__stat">
          <span>Guarantee exposure</span>
          <strong>{data.guaranteeExposure?.length ? data.guaranteeExposure.map((row) => showAmount(row.currency, row.amount)).join(' · ') : 'None'}</strong>
        </article>
        <article className="bg-register__stat">
          <span>Upcoming expiries</span>
          <strong>{data.upcoming?.length || 0}</strong>
        </article>
        <article className="bg-register__stat">
          <span>Release follow-up</span>
          <strong>{data.releaseOverdue?.length || 0}</strong>
        </article>
      </div>
      <section className="bg-register__panel">
        <h2>Recorded instruments</h2>
        {admin.notice}
        <AdminPlainBar admin={admin} rows={instruments} />
        <Table responsive className="bg-register__table">
          <thead><tr>{admin.enabled ? <th /> : null}<th>Type</th><th>Reference</th><th>Amount</th><th>Status</th><th>Expiry</th><th></th></tr></thead>
          <tbody>
            {instruments.length ? instruments.map((row) => (
              <tr key={row._id}>
                <AdminPickCell admin={admin} id={row._id} />
                <td>{row.typeLabel}</td>
                <td>
                  <div className="bg-register__ref">{row.instrumentReference || '—'}</div>
                  <div className="bg-register__meta">{row.issuerName || 'Issuer not recorded'}</div>
                  <EvidenceLinks items={row.documents} />
                </td>
                <td>{showAmount(row.currency, row.currentAmount ?? row.amount)}</td>
                <td><span className="bg-register__status">{row.displayStatus}</span></td>
                <td>{showDate(row.currentExpiryDate || row.expiryDate)}</td>
                <td className="bg-register__actions">
                  <Button size="sm" variant="link" onClick={() => act(row._id, 'extend')}>Extend</Button>
                  <Button size="sm" variant="link" onClick={() => act(row._id, 'release')}>Release</Button>
                  <Button size="sm" variant="link" onClick={() => act(row._id, 'invoked')}>Invoked</Button>
                  <AdminRowDelete admin={admin} id={row._id} label={row.instrumentReference || row.typeLabel || 'Instrument'} />
                </td>
              </tr>
            )) : (
              <tr><td colSpan={admin.enabled ? 7 : 6} className="bg-register__empty">No instruments recorded yet.</td></tr>
            )}
          </tbody>
        </Table>
        {admin.modal}
        <form className="bg-register__form mt-3" onSubmit={async (event) => {
          event.preventDefault();
          try {
            await platformAPI.recordSecurityRefund(refund.instrumentId, {
              kind: refund.kind,
              amount: Number(refund.amount),
              reason: refund.reason,
              reference: refund.reference,
              target: refund.kind === 'adjusted' ? 'performance_security' : undefined,
              destinationInstrumentId: refund.destinationInstrumentId || undefined,
              evidenceDocumentId: (refundEvidence.find((item) => item.documentId) || {}).documentId,
              date: new Date().toISOString().slice(0, 10)
            });
            setError('');
            setRefund({ instrumentId: '', kind: 'received', amount: '', reason: '', reference: '', destinationInstrumentId: '' });
            await load();
          } catch (err) {
            setError(errText(err, 'The refund could not be recorded.'));
          }
        }}>
          <h2>Refund, forfeiture, or adjustment</h2>
          <p>Cash only. A bank guarantee is released, not refunded. An adjustment names the performance security and does not post an accounting entry.</p>
          <div className="bg-register__grid">
            <Form.Group className="bg-register__span-4">
              <Form.Label>Instrument</Form.Label>
              <Form.Select aria-label="Refund instrument" value={refund.instrumentId} onChange={(event) => setRefund((current) => ({ ...current, instrumentId: event.target.value }))}>
                <option value="">Select</option>
                {instruments.filter((row) => row.settlement === 'cash').map((row) => (
                  <option key={row._id} value={row._id}>{row.typeLabel} · {row.currency} {row.amount}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Movement</Form.Label>
              <Form.Select aria-label="Refund movement" value={refund.kind} onChange={(event) => setRefund((current) => ({ ...current, kind: event.target.value }))}>
                <option value="received">Received</option>
                <option value="requested">Requested</option>
                <option value="forfeited">Forfeited</option>
                <option value="adjusted">Adjusted to performance security</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Amount</Form.Label>
              <Form.Control aria-label="Refund amount" value={refund.amount} onChange={(event) => setRefund((current) => ({ ...current, amount: event.target.value }))} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Reference</Form.Label>
              <Form.Control aria-label="Refund reference" value={refund.reference} onChange={(event) => setRefund((current) => ({ ...current, reference: event.target.value }))} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Reason</Form.Label>
              <Form.Control aria-label="Refund reason" value={refund.reason} onChange={(event) => setRefund((current) => ({ ...current, reason: event.target.value }))} />
            </Form.Group>
            {refund.kind === 'adjusted' ? (
              <Form.Group className="bg-register__span-4">
                <Form.Label>Performance security</Form.Label>
                <Form.Select aria-label="Destination performance security" value={refund.destinationInstrumentId} onChange={(event) => setRefund((current) => ({ ...current, destinationInstrumentId: event.target.value }))}>
                  <option value="">Select</option>
                  {instruments.filter((row) => row._id !== refund.instrumentId && (row.settlement === 'guarantee' || (row.settlement === 'cash' && row.typeCode === 'performance_bg'))).map((row) => (
                    <option key={row._id} value={row._id}>{row.settlement === 'cash' ? 'Cash held' : 'Bank guarantee'} · {row.typeLabel} · {row.currency} {row.amount}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}
          </div>
          {refund.kind === 'adjusted' ? <EvidencePicker tenderId={tenderId} documents={refundEvidence} onChange={setRefundEvidence} label="Adjustment evidence" area="bg_emd" /> : null}
          <Button type="submit" size="sm" disabled={!refund.instrumentId || !refund.amount}>Record movement</Button>
        </form>
      </section>
      <form className="bg-register__form" onSubmit={save}>
        <section>
          <h2>Instrument</h2>
          <p>The tender, the kind of security, and the amount in one currency.</p>
          <div className="bg-register__grid">
            {!tenderId ? (
              <Form.Group className="bg-register__span-12">
                <Form.Label>Tender *</Form.Label>
                <div className="bg-register__pair">
                  <Form.Control placeholder="Search by reference or title" value={tenderQuery} onChange={(event) => setTenderQuery(event.target.value)} aria-label="Search tenders" />
                  <Form.Select aria-label="Tender" required value={form.tenderId} onChange={(event) => setForm({ ...form, tenderId: event.target.value })}>
                    <option value="">Select a tender</option>
                    {tenderChoices.map((row) => {
                      const id = row.id || row._id;
                      return <option key={id} value={id}>{row.reference} — {row.title}</option>;
                    })}
                  </Form.Select>
                </div>
              </Form.Group>
            ) : null}
            <Form.Group className="bg-register__span-4">
              <Form.Label>Type</Form.Label>
              <Form.Select aria-label="Instrument type" value={form.typeCode} onChange={(event) => setForm({ ...form, typeCode: event.target.value })}>
                {(data.types || []).filter((row) => row.status === 'active').map((row) => <option key={row.code} value={row.code}>{row.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Amount *</Form.Label>
              <Form.Control type="number" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} required />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Currency</Form.Label>
              <Form.Select aria-label="Currency" value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value })}>
                {currencies.map((code) => <option key={code} value={code}>{code}</option>)}
              </Form.Select>
            </Form.Group>
          </div>
        </section>
        <section>
          <h2>Bank and dates</h2>
          <p>Choose a bank from the master, or leave it blank when the instrument has no issuer yet. A name already stored on an older record stays visible.</p>
          <div className="bg-register__grid">
            <Form.Group className="bg-register__span-6">
              <Form.Label>Bank or issuer</Form.Label>
              <Form.Control placeholder="Search banks and issuers" value={issuerQuery} onChange={(event) => setIssuerQuery(event.target.value)} aria-label="Search banks and issuers" />
              <Form.Select className="mt-2" aria-label="Bank or issuer" value={form.issuerCode} onChange={(event) => {
                const issuer = (data.issuers || []).find((row) => row.code === event.target.value);
                setForm({ ...form, issuerCode: issuer?.code || '', issuerName: issuer?.label || '' });
              }}>
                <option value="">{(data.issuers || []).some((row) => row.status === 'active') ? (issuers.length ? 'Select a bank or issuer' : 'No matching bank') : 'No banks yet'}</option>
                {issuers.map((row) => <option key={row.code} value={row.code}>{row.label}</option>)}
              </Form.Select>
              {!data.issuers?.some((row) => row.status === 'active') ? <Link className="bg-register__hint" to="/apps/tender/masters/issuers">Add a bank in Masters</Link> : null}
            </Form.Group>
            <Form.Group className="bg-register__span-3">
              <Form.Label>Instrument reference</Form.Label>
              <Form.Control placeholder="Bank reference" value={form.instrumentReference} onChange={(event) => setForm({ ...form, instrumentReference: event.target.value })} />
            </Form.Group>
            <Form.Group className="bg-register__span-3">
              <Form.Label>Beneficiary</Form.Label>
              <Form.Control placeholder="Authority or client" value={form.beneficiary} onChange={(event) => setForm({ ...form, beneficiary: event.target.value })} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Issue date</Form.Label>
              <Form.Control type="date" aria-label="Issue date" value={form.issueDate} onChange={(event) => setForm({ ...form, issueDate: event.target.value })} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Expiry</Form.Label>
              <Form.Control type="date" aria-label="Expiry date" value={form.expiryDate} onChange={(event) => setForm({ ...form, expiryDate: event.target.value })} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small">Refund follow-up days</Form.Label>
              <Form.Control type="number" min="0" placeholder="Reminder only. Not the refund due date." value={form.refundFollowUpDays || ''} onChange={(event) => setForm({ ...form, refundFollowUpDays: event.target.value })} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Settlement</Form.Label>
              <Form.Select aria-label="Settlement" value={form.settlement} onChange={(event) => setForm({ ...form, settlement: event.target.value })}>
                <option value="cash">Cash / deposit</option>
                <option value="guarantee">Bank guarantee</option>
                <option value="declaration">Declaration</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Physical deadline</Form.Label>
              <Form.Control type="date" aria-label="Physical deadline" value={form.physicalDueOn} onChange={(event) => setForm({ ...form, physicalDueOn: event.target.value })} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Dispatch address</Form.Label>
              <Form.Control placeholder="Where the instrument is sent" value={form.physicalAddress} onChange={(event) => setForm({ ...form, physicalAddress: event.target.value })} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Tracking reference</Form.Label>
              <Form.Control placeholder="Courier or portal reference" value={form.trackingReference} onChange={(event) => setForm({ ...form, trackingReference: event.target.value })} />
            </Form.Group>
            <Form.Group className="bg-register__span-4">
              <Form.Label>Claim expiry</Form.Label>
              <Form.Control type="date" aria-label="Claim expiry date" value={form.claimExpiryDate} onChange={(event) => setForm({ ...form, claimExpiryDate: event.target.value })} />
            </Form.Group>
          </div>
        </section>
        <section>
          <h2>Supporting documents</h2>
          <p>Upload a file or choose one already stored. A typed name is not a document. These files are not made available to AI.</p>
          <EvidencePicker embedded tenderId={form.tenderId || tenderId} documents={documents} onChange={setDocuments} area="bg_emd" />
          <div className="bg-register__submit">
            <Button type="submit">Record instrument</Button>
          </div>
        </section>
      </form>
    </div>
  );
}

export function PartnersPanel({ tenderId }) {
  const [masters, setMasters] = useState([]);
  const [rows, setRows] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState('');
  const load = () => Promise.all([
    platformAPI.listPartners(),
    platformAPI.listTenderPartners(tenderId)
  ]).then(([masterRes, rowRes]) => {
    setMasters(masterRes.data?.data?.partners || []);
    setRows(rowRes.data?.data?.partners || []);
  }).catch((error) => setError(errText(error, 'Unable to load partners')));
  useEffect(() => { load(); }, [tenderId]);
  const admin = useAdminRecordDelete({
    entity: 'tenderPartner',
    noun: 'partner review',
    plural: 'partner reviews',
    reload: load,
    label: (row) => row.partner?.name || 'Partner review'
  });
  const add = async () => {
    try {
      await platformAPI.addTenderPartner(tenderId, { partnerId, role: 'member', participationType: 'jv' });
      setPartnerId('');
      await load();
    } catch (error) {
      setError(errText(error, 'Unable to add the partner'));
    }
  };
  const review = async (row, reviewStatus) => {
    const comments = window.prompt('Review comment') || '';
    try {
      await platformAPI.reviewTenderPartner(row._id, { reviewStatus, comments, checklist: row.checklist, documents: documents.filter((item) => item.documentId) });
      await load();
    } catch (error) {
      setError(errText(error, 'Unable to save the review'));
    }
  };
  return (
    <div>
      <p className="small text-muted">Manual review of documents supplied for this tender. This is not a legal, credit, sanctions, or regulatory verification. The partner master stays separate from this review.</p>
      {error ? <Alert variant="warning">{error}</Alert> : null}
      {admin.notice}
      <AdminPlainBar admin={admin} rows={rows} />
      <Table size="sm">
        <thead><tr>{admin.enabled ? <th /> : null}<th>Partner</th><th>Role</th><th>Review</th><th></th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id}>
              <AdminPickCell admin={admin} id={row._id} />
              <td>{row.partner?.name || 'Partner'}{row.partner?.isActive === false ? <span className="cust-status cust-status--inactive"> (inactive)</span> : ''}</td>
              <td>{row.role}</td>
              <td>{row.reviewStatus}{row.comments ? ` — ${row.comments}` : ''}<div className="text-muted">{(row.checklist || []).map((item) => `${item.label} (${item.status})`).join(' · ')}</div><EvidenceLinks items={row.documents} /></td>
              <td>
                <Button size="sm" variant="link" onClick={() => review(row, 'approved')}>Approve</Button>
                <Button size="sm" variant="link" onClick={() => review(row, 'not_approved')}>Not approved</Button>
                <AdminRowDelete admin={admin} id={row._id} label={row.partner?.name || 'Partner review'} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {admin.modal}
      <div className="d-flex gap-2">
        <Form.Select value={partnerId} onChange={(event) => setPartnerId(event.target.value)}>
          <option value="">Select an active partner</option>
          {masters.filter((row) => row.isActive !== false).map((row) => <option key={row._id} value={row._id}>{row.code} — {row.name}</option>)}
        </Form.Select>
        <Button size="sm" onClick={add} disabled={!partnerId}>Add</Button>
      </div>
      <div className="mt-3"><EvidencePicker tenderId={tenderId} documents={documents} onChange={setDocuments} label="Due-diligence evidence" area="partner" /></div>
    </div>
  );
}

export function QueriesPanel({ tenderId }) {
  const [rows, setRows] = useState([]);
  const [question, setQuestion] = useState('');
  const [deadline, setDeadline] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');
  const load = () => platformAPI.listPreBidQueries(tenderId ? { tenderId } : {}).then((response) => setRows(response.data?.data?.queries || [])).catch((error) => setError(errText(error, 'Unable to load queries')));
  useEffect(() => { load(); }, [tenderId]);
  const admin = useAdminRecordDelete({
    entity: 'prebid',
    noun: 'query',
    plural: 'queries',
    reload: load,
    label: (row) => row.reference || row.question
  });
  const save = async (event) => {
    event.preventDefault();
    try {
      await platformAPI.savePreBidQuery({ tenderId, question, deadline, status: 'draft', attachments: attachments.filter((item) => item.documentId) });
      setQuestion('');
      setAttachments([]);
      await load();
    } catch (error) {
      setError(errText(error, 'Unable to save the query'));
    }
  };
  return (
    <div>
      <p className="small text-muted">Pre-bid questions stay separate from clarifications raised after submission. Sending the question to the authority is done outside this application. Record the communication reference and the response here.</p>
      {error ? <Alert variant="warning">{error}</Alert> : null}
      {admin.notice}
      <AdminPlainBar admin={admin} rows={rows} />
      <Table size="sm">
        <thead><tr>{admin.enabled ? <th /> : null}<th>Reference</th><th>Question</th><th>Status</th><th>Deadline</th><th /></tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row._id}>
            <AdminPickCell admin={admin} id={row._id} />
            <td>{row.reference}</td>
            <td>{row.question}<EvidenceLinks items={row.attachments} /></td>
            <td>{row.status}</td>
            <td>{row.deadline ? String(row.deadline).slice(0, 10) : '—'}</td>
            <td><AdminRowDelete admin={admin} id={row._id} label={row.reference || 'Query'} /></td>
          </tr>
        ))}</tbody>
      </Table>
      {admin.modal}
      {tenderId ? (
        <form className="row g-2" onSubmit={save}>
          <div className="col-md-7"><Form.Control placeholder="Question" value={question} onChange={(event) => setQuestion(event.target.value)} required /></div>
          <div className="col-md-3"><Form.Control type="date" value={deadline} onChange={(event) => setDeadline(event.target.value)} /></div>
          <div className="col-12"><EvidencePicker tenderId={tenderId} documents={attachments} onChange={setAttachments} label="Query attachments" area="prebid" /></div>
          <div className="col-md-2"><Button size="sm" type="submit">Save draft</Button></div>
        </form>
      ) : null}
    </div>
  );
}

export function CorrigendaPanel({ tenderId }) {
  const [rows, setRows] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState({ number: '', summary: '', proposedDeadline: '' });
  const [error, setError] = useState('');
  const load = () => platformAPI.listCorrigenda(tenderId ? { tenderId } : {}).then((response) => setRows(response.data?.data?.corrigenda || [])).catch((error) => setError(errText(error, 'Unable to load corrigenda')));
  useEffect(() => { load(); }, [tenderId]);
  const admin = useAdminRecordDelete({
    entity: 'corrigendum',
    noun: 'corrigendum',
    plural: 'corrigenda',
    reload: load,
    label: (row) => row.number || 'Corrigendum'
  });
  const save = async (event) => {
    event.preventDefault();
    try {
      const file = documents.find((item) => item.documentId);
      await platformAPI.saveCorrigendum({ ...form, tenderId, documentId: file?.documentId });
      setForm({ number: '', summary: '', proposedDeadline: '' });
      setDocuments([]);
      await load();
    } catch (error) {
      setError(errText(error, 'Unable to record the corrigendum'));
    }
  };
  const confirm = async (id) => {
    if (!window.confirm('Confirm the new submission deadline? The previous date stays in history.')) return;
    try {
      await platformAPI.confirmCorrigendumDeadline(id);
      await load();
    } catch (error) {
      setError(errText(error, 'Unable to confirm the deadline'));
    }
  };
  return (
    <div>
      <Alert variant="light" className="small">Alerts apply to corrigenda entered here and to deadlines already recorded on the tender. This application does not watch tender portals or discover amendments on its own. Email is not marked delivered.</Alert>
      {error ? <Alert variant="warning">{error}</Alert> : null}
      {admin.notice}
      <AdminPlainBar admin={admin} rows={rows} />
      <Table size="sm">
        <thead><tr>{admin.enabled ? <th /> : null}<th>Number</th><th>Summary</th><th>Deadline</th><th></th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id}>
              <AdminPickCell admin={admin} id={row._id} />
              <td>{row.number}</td>
              <td>{row.summary}<div className="text-muted">In-app notice. Email was not sent.</div><EvidenceLinks items={row.documentId || row.documentWithheld ? [{ documentId: row.documentId, name: row.documentName, version: row.documentVersion, withheld: row.documentWithheld }] : []} /></td>
              <td>{row.deadlineChange?.status === 'none' ? '—' : `${row.deadlineChange?.status}: ${row.deadlineChange?.proposed ? String(row.deadlineChange.proposed).slice(0, 10) : ''}`}</td>
              <td>
                {row.deadlineChange?.status === 'proposed' ? <Button size="sm" variant="link" onClick={() => confirm(row._id)}>Confirm deadline</Button> : null}
                <AdminRowDelete admin={admin} id={row._id} label={row.number || 'Corrigendum'} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {admin.modal}
      {tenderId ? (
        <form className="row g-2" onSubmit={save}>
          <div className="col-md-3"><Form.Control placeholder="Number" value={form.number} onChange={(event) => setForm({ ...form, number: event.target.value })} required /></div>
          <div className="col-md-5"><Form.Control placeholder="Summary of changes" value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} required /></div>
          <div className="col-md-2"><Form.Control type="date" value={form.proposedDeadline} onChange={(event) => setForm({ ...form, proposedDeadline: event.target.value })} /></div>
          <div className="col-12"><EvidencePicker tenderId={tenderId} documents={documents} onChange={(next) => setDocuments(next.slice(-1))} label="Corrigendum document" area="corrigendum" /></div>
          <div className="col-md-2"><Button size="sm" type="submit">Record</Button></div>
        </form>
      ) : null}
    </div>
  );
}

const decisionLabel = (value) => ({
  go: 'Go',
  conditional_go: 'Conditional go',
  no_go: 'No-go'
}[value] || 'No recommendation yet');

const reviewLabel = (value) => ({
  draft: 'Draft',
  submitted: 'Waiting for approval',
  returned: 'Returned for revision',
  approved: 'Approved'
}[value] || value || 'Draft');

export function ScorePanel({ tenderId, onChanged }) {
  const { allowed } = useWorkspaceAccess();
  const canScore = allowed(['tender.eligibility.edit']);
  const canAuthorise = allowed(['tender.decision.approve']);
  const [data, setData] = useState(null);
  const [profileId, setProfileId] = useState('');
  const [scores, setScores] = useState({});
  const [gates, setGates] = useState({});
  const [error, setError] = useState('');
  const [finalDecision, setFinalDecision] = useState('go');
  const [outcomeReasonCode, setOutcomeReasonCode] = useState('');
  const [outcomeRemarks, setOutcomeRemarks] = useState('');
  const [reasons, setReasons] = useState([]);
  useEffect(() => {
    if (finalDecision !== 'no_go') return undefined;
    platformAPI.listReference('outcome-reasons', { outcome: 'no_go', keep: outcomeReasonCode || undefined })
      .then((response) => setReasons(response.data?.data?.values || []))
      .catch(() => setReasons([]));
    return undefined;
  }, [finalDecision, outcomeReasonCode]);
  const [finalRationale, setFinalRationale] = useState('');
  const [differsReason, setDiffersReason] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');
  const load = () => platformAPI.getTenderAssessment(tenderId).then((response) => {
    const next = response.data?.data;
    setData(next);
    const assessment = next?.assessment;
    if (assessment?.profileId) setProfileId(assessment.profileId);
    const map = {};
    (assessment?.scores || []).forEach((row) => { map[row.code] = row.score ?? ''; });
    setScores(map);
    const gateMap = {};
    (assessment?.gates || []).forEach((row) => { gateMap[row.code] = row.result && row.result !== 'unresolved' ? row.result : ''; });
    setGates(gateMap);
  }).catch((error) => setError(errText(error, 'Unable to load the score')));
  useEffect(() => { load(); }, [tenderId]);
  if (!data) return error ? <Alert variant="warning">{error}</Alert> : <Spinner animation="border" size="sm" />;
  const profile = (data.profiles || []).find((row) => String(row._id) === String(profileId || data.assessment?.profileId)) || data.profiles?.[0];
  const criteria = data.assessment?.snapshot?.criteria || profile?.criteria || [];
  const gateDefs = data.assessment?.snapshot?.gates || profile?.gates || [];
  const locked = ['submitted', 'approved'].includes(data.assessment?.status);
  const finish = async () => {
    await load();
    if (onChanged) onChanged();
  };
  const save = async () => {
    setBusy('save');
    setError('');
    try {
      await platformAPI.saveTenderAssessment(tenderId, {
        id: data.assessment?._id,
        profileId: profile?._id,
        scores: criteria.map((row) => ({ code: row.code, score: scores[row.code] === '' || scores[row.code] == null ? null : Number(scores[row.code]), evidence: '' })),
        gates: gateDefs.map((row) => ({ code: row.code, result: gates[row.code] || '' }))
      });
      await finish();
    } catch (error) {
      setError(errText(error, 'Unable to save scores'));
    } finally {
      setBusy('');
    }
  };
  const submit = async () => {
    setBusy('submit');
    setError('');
    try {
      await platformAPI.submitTenderAssessment(data.assessment._id);
      await finish();
    } catch (error) {
      setError(errText(error, 'Unable to submit'));
    } finally {
      setBusy('');
    }
  };
  const approve = async () => {
    if (!finalRationale.trim()) {
      setError('A rationale is required before the final decision is recorded.');
      return;
    }
    setBusy('approve');
    setError('');
    try {
      await platformAPI.approveTenderAssessment(data.assessment._id, {
        finalDecision,
        finalRationale,
        differsReason,
        outcomeReasonCode: finalDecision === 'no_go' ? outcomeReasonCode : '',
        outcomeRemarks: finalDecision === 'no_go' ? outcomeRemarks : ''
      });
      await finish();
    } catch (error) {
      setError(errText(error, 'Unable to approve'));
    } finally {
      setBusy('');
    }
  };
  const sendBack = async () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    setBusy('return');
    setError('');
    try {
      await platformAPI.returnTenderAssessment(assessment._id, { reason: reason.trim() });
      setReason('');
      await finish();
    } catch (error) {
      setError(errText(error, 'Unable to return the assessment'));
    } finally {
      setBusy('');
    }
  };
  const assessment = data.assessment;
  return (
    <div>
      <p className="small text-muted">The weighted score is calculated on the server. A missing score stays incomplete and is not treated as zero. A failed eligibility gate cannot be outweighed by a high score. The calculated recommendation, the submitted proposal, and Dhruv’s final decision are separate. Sample weights are not a confirmed company policy.</p>
      {error ? <Alert variant="warning">{error}</Alert> : null}
      <Form.Select className="mb-2" value={profile?._id || ''} onChange={(event) => setProfileId(event.target.value)} disabled={Boolean(assessment?.snapshot)}>
        {(data.profiles || []).map((row) => <option key={row._id} value={row._id}>{row.name} v{row.version}{row.isSample ? ' (sample)' : ''}</option>)}
      </Form.Select>
      {assessment?.snapshot ? <p className="small">Using {assessment.snapshot.name} version {assessment.snapshot.version}. Later changes to the master profile do not change this assessment.</p> : null}
      {criteria.map((row) => (
        <div key={row.code} className="row g-2 mb-2 align-items-center">
          <div className="col-md-6">{row.name} <span className="text-muted">{row.weight}% · max {row.maxScore}</span></div>
          <div className="col-md-3"><Form.Control type="number" min="0" max={row.maxScore} value={scores[row.code] ?? ''} disabled={locked} onChange={(event) => setScores({ ...scores, [row.code]: event.target.value })} /></div>
        </div>
      ))}
      {gateDefs.map((row) => (
        <div key={row.code} className="row g-2 mb-2 align-items-center">
          <div className="col-md-6">{row.label} <span className="text-muted">Mandatory gate</span></div>
          <div className="col-md-3">
            <Form.Select value={gates[row.code] || ''} disabled={locked} onChange={(event) => setGates({ ...gates, [row.code]: event.target.value })}>
              <option value="">Unresolved</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
            </Form.Select>
          </div>
        </div>
      ))}
      {assessment ? (
        <div className="border rounded p-2 mb-3 small">
          <div><strong>Calculated score.</strong> {assessment.complete ? assessment.weightedScore : 'Incomplete'}</div>
          <div><strong>Calculated recommendation.</strong> {assessment.calculatedRecommendation ? decisionLabel(assessment.calculatedRecommendation) : 'No recommendation yet'}</div>
          <div><strong>Review status.</strong> {reviewLabel(assessment.status)}</div>
          <div><strong>Final authorised decision.</strong> {assessment.status === 'approved' && assessment.finalDecision ? `${decisionLabel(assessment.finalDecision)}${assessment.finalRationale ? ` — ${assessment.finalRationale}` : ''}` : 'None. This assessment does not clear the tender to proceed.'}</div>
          {assessment.status === 'returned' ? <Alert variant="warning" className="mt-2 mb-0">Returned for revision. This is not an approval and does not clear the tender to proceed.</Alert> : null}
          {assessment.history?.length ? (
            <ul className="mb-0 mt-2">
              {assessment.history.map((item, index) => (
                <li key={`${item.at}-${index}`}>{reviewLabel(item.action) === item.action ? item.action : item.action}{item.note ? ` — ${item.note}` : ''}{item.at ? ` · ${new Date(item.at).toLocaleString('en-IN')}` : ''}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {canScore ? (
        <div className="d-flex gap-2 mb-3">
          <Button size="sm" onClick={save} disabled={locked || Boolean(busy)}>{busy === 'save' ? 'Saving…' : 'Save scores'}</Button>
          <Button size="sm" variant="outline-primary" onClick={submit} disabled={!assessment || locked || Boolean(busy)}>{busy === 'submit' ? 'Saving…' : 'Submit for approval'}</Button>
        </div>
      ) : null}
      {assessment?.status === 'submitted' && (canScore || canAuthorise) ? (
        <div className="d-flex gap-2 mb-3 align-items-center">
          <Form.Control style={{ maxWidth: '24rem' }} placeholder="Reason required to return" value={reason} onChange={(event) => setReason(event.target.value)} />
          <Button size="sm" variant="outline-primary" onClick={sendBack} disabled={Boolean(busy)}>{busy === 'return' ? 'Saving…' : 'Return for revision'}</Button>
        </div>
      ) : null}
      {assessment?.status === 'approved' && canAuthorise ? (
        <div className="d-flex gap-2 mb-3 align-items-center">
          <Form.Control style={{ maxWidth: '24rem' }} placeholder="Reason required to reopen" value={reason} onChange={(event) => setReason(event.target.value)} />
          <Button size="sm" variant="outline-primary" onClick={sendBack} disabled={Boolean(busy)}>{busy === 'return' ? 'Saving…' : 'Reopen for revision'}</Button>
        </div>
      ) : null}
      {assessment?.status === 'submitted' && canAuthorise ? (
        <div className="row g-2">
          <div className="col-md-3">
            <Form.Select value={finalDecision} onChange={(event) => setFinalDecision(event.target.value)}>
              <option value="go">Go</option>
              <option value="conditional_go">Conditional go</option>
              <option value="no_go">No-Go</option>
            </Form.Select>
          </div>
          {finalDecision === 'no_go' ? (
            <>
              <div className="col-md-4">
                <Form.Select aria-label="Outcome reason" value={outcomeReasonCode} onChange={(event) => setOutcomeReasonCode(event.target.value)}>
                  <option value="">Reason</option>
                  {reasons.filter((row) => row.status === 'active' || row.code === outcomeReasonCode).map((row) => (
                    <option key={row.code} value={row.code}>{row.label}{row.status === 'inactive' ? ' (inactive)' : ''}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Control placeholder={outcomeReasonCode === 'other' ? 'Remarks required for Other' : 'Remarks'} value={outcomeRemarks} onChange={(event) => setOutcomeRemarks(event.target.value)} />
              </div>
            </>
          ) : null}
          <div className="col-md-4"><Form.Control placeholder="Final rationale" value={finalRationale} onChange={(event) => setFinalRationale(event.target.value)} /></div>
          <div className="col-md-3"><Form.Control placeholder="Reason if this differs" value={differsReason} onChange={(event) => setDiffersReason(event.target.value)} /></div>
          <div className="col-md-2"><Button size="sm" onClick={approve} disabled={Boolean(busy)}>{busy === 'approve' ? 'Saving…' : 'Record decision'}</Button></div>
        </div>
      ) : null}
    </div>
  );
}
